import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../components/PricingPage.css';
import './PricingRoute.css';
import {
  AppHeader,
  PricingPage,
  AuthModal,
  ServerManagementModal,
  SubscriptionModal,
  BetaModeModal,
  useServerManagement,
} from '../components';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { isFeatureEnabled } from '../utils/featureFlags';

export const PricingRoute: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const subscription = useSubscription();
  const serverManagement = useServerManagement();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup');
  const [showServerManagement, setShowServerManagement] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [betaModalFeature, setBetaModalFeature] = useState('');
  const [pendingBetaAction, setPendingBetaAction] = useState<(() => void) | null>(null);

  const handleShowAuthModal = (mode: 'signin' | 'signup' = 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleShowBetaModal = (featureName: string, onContinue?: () => void) => {
    setBetaModalFeature(featureName);
    setPendingBetaAction(() => onContinue);
    setShowBetaModal(true);
  };

  const handleCloseBetaModal = () => {
    setShowBetaModal(false);
    setBetaModalFeature('');
    setPendingBetaAction(null);
  };

  const handleContinueToFeature = () => {
    setShowBetaModal(false);
    setBetaModalFeature('');
    if (pendingBetaAction) {
      pendingBetaAction();
    }
    setPendingBetaAction(null);
  };

  const handleManageServers = () => {
    if (isFeatureEnabled('ENABLE_PAYWALL')) {
      if (!subscription.hasActiveSubscription) {
        setShowSubscriptionModal(true);
        return;
      }
    } else {
      handleShowBetaModal(t('serverManagement', { ns: 'features' }), () => {
        setShowServerManagement(true);
      });
      return;
    }

    setShowServerManagement(true);
  };

  React.useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [user, showAuthModal]);

  return (
    <div className="App pricing-route">
      <AppHeader
        user={user}
        activeServer={serverManagement.getActiveServer()}
        onShowAuthModal={handleShowAuthModal}
        onManageServers={handleManageServers}
        currentPage="pricing"
      />

      <main className="pricing-route-main App-main">
        <PricingPage onSignUp={() => handleShowAuthModal('signup')} />
      </main>

      <AuthModal
        isVisible={showAuthModal}
        onClose={handleCloseAuthModal}
        initialMode={authMode}
      />

      <ServerManagementModal
        isVisible={showServerManagement}
        onClose={() => setShowServerManagement(false)}
        user={user}
        savedServers={serverManagement.savedServers}
        activeServerId={serverManagement.activeServerId}
        onSaveServers={serverManagement.saveServers}
        onSetActiveServer={serverManagement.setActiveServer}
        onRemoveServer={serverManagement.removeServer}
        onRemoveBudgetPassword={serverManagement.removeBudgetPassword}
        onLoadBudgetsForServer={serverManagement.loadBudgetsForServer}
        onValidateBudgetAccess={serverManagement.validateBudgetAccess}
      />

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSuccess={() => {
          setShowSubscriptionModal(false);
          subscription.refetch();
        }}
      />

      <BetaModeModal
        isVisible={showBetaModal}
        onClose={handleCloseBetaModal}
        onContinue={handleContinueToFeature}
        featureName={betaModalFeature}
      />
    </div>
  );
};
